import React from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from '@mui/material';
import {getEntityTypeLabel} from '../../../utils/entityTypeMeta';
import {getAwpApiBase} from '../../../utils/awpApiBase';
import {getAwpClientHeaders} from '../../../session/awpRequestHeaders';

const ENTITY_TYPES = ['PERSON', 'ORG', 'GPE', 'LOC', 'EVENT', 'PRODUCT', 'NORP', 'UNKNOWN'];

function parseAliases(raw) {
    if (!raw || typeof raw !== 'string') return [];
    return [...new Set(raw.split(',').map((s) => s.trim()).filter(Boolean))];
}

/**
 * Dialog to promote an Entity Candidate (NER-detected, source="entities") to a Custom Entity.
 * Pre-fills from the entity already loaded on the profile page.
 */
export default function PromoteEntityCandidateDialog({open, onClose, onPromoted, entity, variant = 'custom'}) {
    const isWikidataAdopt = variant === 'wikidata';
    const [canonicalName, setCanonicalName] = React.useState('');
    const [entityType, setEntityType]       = React.useState('UNKNOWN');
    const [description, setDescription]     = React.useState('');
    const [aliasesRaw, setAliasesRaw]       = React.useState('');
    const [touched, setTouched]             = React.useState(false);
    const [saving, setSaving]               = React.useState(false);
    const [error, setError]                 = React.useState(null);

    // Pre-fill from the entity when the dialog opens
    React.useEffect(() => {
        if (!open) return;
        setCanonicalName(entity?.canonicalName ?? '');
        const incomingType = entity?.entityType ?? 'UNKNOWN';
        setEntityType(isWikidataAdopt && String(incomingType).toUpperCase() === 'UNKNOWN'
            ? 'GPE'
            : incomingType);
        setDescription(entity?.description ?? '');
        setAliasesRaw(Array.isArray(entity?.aliases) ? entity.aliases.join(', ') : '');
        setTouched(false);
        setSaving(false);
        setError(null);
    }, [open, entity]);

    const nameError = touched && !canonicalName.trim() ? 'Name is required' : '';

    const handleClose = () => {
        if (saving) return;
        onClose?.();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setTouched(true);
        const trimmedName = canonicalName.trim();
        if (!trimmedName) return;

        setSaving(true);
        setError(null);
        try {
            const entityId = entity?.wikidataId || entity?.id;
            const action = isWikidataAdopt ? 'adopt' : 'promote';
            const res = await fetch(`${getAwpApiBase()}/api/entities/${encodeURIComponent(entityId)}/${action}`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json', ...getAwpClientHeaders()},
                body: JSON.stringify({
                    canonicalName: trimmedName,
                    entityType,
                    description: description.trim() || undefined,
                    aliases: parseAliases(aliasesRaw),
                }),
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.error || `HTTP ${res.status}`);
            }
            const result = await res.json();
            onPromoted?.(result);
            onClose?.();
        } catch (err) {
            setError(err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>
                    {isWikidataAdopt ? 'Promote to Knowledge Base' : 'Promote to Custom Entity'}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{pt: 1}}>
                        {error ? (
                            <Alert severity="error">{error.message || 'Promotion failed.'}</Alert>
                        ) : null}

                        <TextField
                            autoFocus
                            required
                            label="Name"
                            value={canonicalName}
                            onChange={(e) => setCanonicalName(e.target.value)}
                            disabled={saving}
                            error={Boolean(nameError)}
                            helperText={nameError || (isWikidataAdopt
                                ? 'If another Place already uses this name, add a disambiguator (e.g. district)'
                                : 'Canonical display name in the knowledge base')}
                            fullWidth
                        />

                        <FormControl fullWidth disabled={saving}>
                            <InputLabel id="promote-entity-type-label">Type</InputLabel>
                            <Select
                                labelId="promote-entity-type-label"
                                label="Type"
                                value={entityType}
                                onChange={(e) => setEntityType(e.target.value)}
                            >
                                {ENTITY_TYPES.filter((type) => !isWikidataAdopt || type !== 'UNKNOWN').map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {getEntityTypeLabel(type) || type}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            disabled={saving}
                            multiline
                            minRows={2}
                            fullWidth
                        />

                        <TextField
                            label="Aliases"
                            value={aliasesRaw}
                            onChange={(e) => setAliasesRaw(e.target.value)}
                            disabled={saving}
                            helperText="Comma-separated alternate names"
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={saving}>Cancel</Button>
                    <Button type="submit" variant="contained" color="primary" disabled={saving}>
                        {saving ? 'Promoting…' : (isWikidataAdopt ? 'Promote to Knowledge Base' : 'Promote to Custom Entity')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}
